import axios from "axios";
import * as Sentry from "@sentry/react";
import ReactGA from "react-ga4";

// Type definitions for the function parameters
interface AttributionData {
  currentUrl: string;
  referrer?: string;
  fbclid?: string | null;
  utm_campaign?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
  username?: string | null;
  phone_number?: string | null;
  eventName: string | null;
  additionalData?: {
    success?: boolean;
    sale?: {
      product_type?: string;
      amount_inr?: number;
    };
  } | null;
}

const convertAmountToINR = (amountInPaise: number) => {
  return amountInPaise / 100;
};

// GA4 Configuration
const GA4_MEASUREMENT_ID = "G-ZGFSQKYL39";

// Helper function to get a cookie by name
export const getCookie = (name: string): string | null => {
  const cookieArr = document.cookie.split(";");
  for (const cookie of cookieArr) {
    const [key, value] = cookie.trim().split("=");
    if (key === name) {
      return value;
    }
  }
  return null; // Return null if the cookie does not exist
};

// Helper function to set a cookie
export const setCookie = (name: string, value: string, days: number) => {
  const expires = new Date();
  expires.setDate(expires.getDate() + days);
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; domain=nationalfinanceolympiad.com; secure; SameSite=None`;
  console.log(
    "cookie set",
    `${name}=${value}; expires=${expires.toUTCString()}; path=/; domain=nationalfinanceolympiad.com; secure; SameSite=None`
  );
};

// Send GA4 event - GA4 will automatically use the configured client_id/user_id
const sendGA4Event = (
  eventName: string,
  parameters: Record<string, any> = {}
) => {
  if (typeof window === "undefined") {
    console.warn("Window not available");
    return;
  }

  // Build event payload with optional parameters
  const eventPayload = {
    category: parameters.category || eventName, // Use provided category or fallback to eventName
    action: eventName,
    label: parameters.label || eventName,
    ...(parameters.product_type && { product_type: parameters.product_type }),
    ...(parameters.amount_inr && { value: parameters.amount_inr }),
  };

  // Use react-ga4 to send the event - it will use the configured client_id/user_id
  ReactGA.event(eventPayload);

  console.log("GA4 event sent with attribution session_id:", eventName);
};

export const sendAttributionData = async ({
  currentUrl = "",
  referrer = "",
  fbclid = null,
  utm_campaign = null,
  utm_source = null,
  utm_medium = null,
  utm_term = null,
  utm_content = null,
  username = null,
  phone_number = null,
  eventName = null,
  additionalData = null,
}: AttributionData): Promise<any> => {
  if (!currentUrl) {
    console.error("currentUrl is required");
    return;
  }

  // Retrieve session_id from cookies
  const session_id = getCookie("session_id") || null;

  try {
    const url = new URL(currentUrl);

    const data = {
      currentUrl: url.hostname,
      currentPath: url.pathname,
      referrer,
      fbclid,
      utm_campaign,
      utm_source,
      utm_medium,
      utm_term,
      utm_content,
      additional_data: {
        event_name: eventName,
        ...additionalData,
      },
    };

    const payload = {
      username,
      session_id,
      phone_number,
      data,
    };

    const response = await axios.post(
      "https://nfo-arbitrium.el.r.appspot.com/channelAttribution/store-attribution",
      payload,
      {
        headers: {
          "x-api-key": "lZxTPXggnP4ygq4PKaVjCQWdRVCYFrz4uxwWf3H3",
          "Content-Type": "application/json",
        },
      }
    );

    console.log("API Response:", response.data);

    if (response.data.session_id && !session_id) {
      setCookie("session_id", response.data.session_id, 30);
      console.log("New session_id saved in cookies:", response.data.session_id);
    }

    // Attribution data sent to backend API - no need to duplicate in GA4
    // GA4 events are handled separately in trackData function

    return response.data;
  } catch (err: any) {
    console.error("Error sending attribution data:", err);
    throw err;
  }
};

export const trackData = async (
  eventName: string | null,
  userName?: string | null,
  phoneNumber?: string | null,
  additionalData?: {
    success?: boolean;
    payment_type?: string;
    sale?: {
      product_type?: string;
      amount_inr?: number;
      order_id?: string;
    };
    ga4Parameters?: {
      label?: string | null;
      category?: string | null;
    };
  }
) => {
  try {
    const response = await sendAttributionData({
      currentUrl: window.location.href,
      referrer: document.referrer,
      fbclid: new URLSearchParams(window.location.search).get("fbclid"),
      utm_campaign: new URLSearchParams(window.location.search).get(
        "utm_campaign"
      ),
      utm_term: new URLSearchParams(window.location.search).get("utm_term"),
      utm_content: new URLSearchParams(window.location.search).get(
        "utm_content"
      ),
      utm_source: new URLSearchParams(window.location.search).get("utm_source"),
      utm_medium: new URLSearchParams(window.location.search).get("utm_medium"),
      username: userName,
      phone_number: phoneNumber,
      eventName: eventName,
      additionalData: additionalData,
    });

    // Send GA4 event for the specific tracking event
    if (eventName) {
      // Send the main event with the event name as category (following existing pattern)
      sendGA4Event(eventName, {
        product_type: additionalData?.sale?.product_type,
        amount_inr: additionalData?.sale?.amount_inr
          ? convertAmountToINR(additionalData.sale.amount_inr)
          : undefined,
        label: additionalData?.ga4Parameters?.label || eventName,
        category: additionalData?.ga4Parameters?.category || eventName,
      });
    }

    console.log("Tracking response:", response);
  } catch (err) {
    console.error("Error tracking data:", err);
    Sentry.captureException(err);
  }
};

export const getEventNameFromItemIds = (itemIds: string): string => {
  console.log("itemids value", itemIds);
  const ids = itemIds.split("-").map(Number);

  // If NFO Registration (3) is present with other items
  if (ids.includes(3)) {
    if (ids.includes(1) && ids.includes(2)) {
      return "nfo_registration_with_personal_finance_handbook_grade_6_8_and_10_12_payment_gateway";
    } else if (ids.includes(1)) {
      return "nfo_registration_with_personal_finance_handbook_grade_6_8_payment_gateway";
    } else if (ids.includes(2)) {
      return "nfo_registration_with_personal_finance_handbook_grade_10_12_payment_gateway";
    }
  }

  // If only single item
  if (ids.length === 1) {
    switch (ids[0]) {
      case 1:
        return "personal_finance_handbook_grade_6_8_payment_gateway";
      case 2:
        return "personal_finance_handbook_grade_10_12_payment_gateway";
      default:
        return "unknown_payment_gateway";
    }
  }

  // If multiple items without NFO Registration
  if (ids.includes(1) && ids.includes(2)) {
    return "personal_finance_handbook_grade_6_8_and_10_12_payment_gateway";
  }

  return "unknown_payment_gateway";
};

// Store order-session mapping for backend webhook handling
export const storeOrderSessionMapping = async (
  orderId: string,
  sessionId: string,
  userId?: string,
  amount?: number,
  currency: string = "INR",
  productType?: string
) => {
  try {
    const response = await axios.post(
      "https://your-backend-domain.com/api/order-session-mapping", // Replace with your actual backend URL
      {
        orderId,
        sessionId,
        userId,
        amount,
        currency,
        productType,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    console.log("Order-session mapping stored:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error storing order-session mapping:", error);
    throw error;
  }
};

// Export GA4 functions for external use
export const sendGA4EventDirectly = sendGA4Event;
export const getSessionId = async (): Promise<string | null> => {
  // First check if session_id exists in cookies
  const sessionId = getCookie("session_id");
  console.log("sessionId from cookies", sessionId);
  if (sessionId) {
    console.log("Session ID from cookies:", sessionId);
    return sessionId;
  }

  // If not in cookies, get from attribution system
  try {
    const response = await axios.get(
      "https://nfo-arbitrium.el.r.appspot.com/channelAttribution/get-session-id"
    );
    console.log(
      "Session ID from attribution system:",
      response.data.session_id ? "true" : "false"
    );
    if (response.data.session_id)
      setCookie("session_id", response.data.session_id, 30);
    return response.data.session_id;
  } catch (error) {
    console.error("Error getting session ID from attribution system:", error);
    return null;
  }
};
